import styled from '@emotion/styled';
import { observer } from 'mobx-react';
import Router from 'next/router';
import NextError from 'next/error';
import { Component } from 'react';
import dynamic from 'next/dynamic';
import { pushGAEvent } from '@widget/analytics';
import { OverlayCmp } from '@toolbox/components';
import { createWebSettingsQuery } from '@widget/api/storyblok';
import { getLocaleForWidget, getMarket } from '@widget/config/market';
import config from 'next/config';

import { Layer } from '@graphql';
import { ContentSection } from '@components/ContentSection';
import { Header } from '@components/Header';
import { Footer } from '@components/Footer';
import { GradientScale } from '@components/GradientScale';
import { Layout } from '@components/Layout';
import { ReviewBar } from '@components/ReviewBar';
import { GetInTouchBanner } from '@components/GetInTouchBanner';
import { Map } from '@components/Map';
import { Modal } from '@components/Modal';
import { MAPBOX_ACCESS_TOKEN, MAPBOX_IMAGE_BASE_URL, ZOOM_LEVELS } from '@lib/const';
import { filterViewModel } from '@lib/filterViewModel';
import { MapPlace } from '@lib/GoogleMapsService/MapPlace';
import { moveMap } from '@lib/MapHelper';
import { getMetaContent } from '@lib/metaContent';
import getFilterStore from '@stores/FilterStore';
import getLayerStore from '@stores/LayerStore';
import getUiStore from '@stores/UIStore';
import { defaultTheme } from '@lib/themes';
import { sanitizeQueryParam } from '@lib/utils';
import { createApolloClient } from '@lib/apolloClient';
import { GetInTouchButton } from '@components/GetInTouchBanner/GetInTouchButton';
import { getFAQData } from '@lib/getFAQData';

import type { FAQSectionData } from '@components/FAQSection';
import type { LocaleValue } from '@widget/config/host';
import type { NormalizedCacheObject } from '@apollo/client';
import type {
  GetInfoByAreaQuery,
  GetInfoByAreaQueryVariables,
  GetNearbyAreasQuery,
  GetNearbyAreasQueryVariables,
  GetTrendsQuery,
  ParentSearch,
} from '@graphql';
import type { WidgetProps } from '@widget';
import type { GetInfoByArea, LatLngCoordinates, LayerInfo } from '~/lib/types';
import type { NextPageContext } from 'next';
import type { THeaderSettings } from '@widget/api/storyblok';

const WidgetX = dynamic<WidgetProps>(
  () => import(/* webpackChunkName: 'widget' */ '@widget').then((mod) => mod.Widget),
  {
    ssr: false,
  },
);

const LayerStore = getLayerStore();
const UIStore = getUiStore();
const FilterStore = getFilterStore();
const getWebSettings = createWebSettingsQuery(config().publicRuntimeConfig.storyblokWebKey);

function sendImpressionEvent() {
  pushGAEvent('custom-event', { stepName: 'x_impression' });
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  max-height: 100%;
  overflow: hidden;

  > .review-bar {
    display: none;
  }

  @media (min-width: ${(props) => props.theme.breakpoints.tablet_landscape}) {
    height: 100vh;
  }
  @media (min-width: ${(props) => props.theme.breakpoints.desktop}) {
    > .review-bar {
      display: flex;
    }
  }
`;

type UrlQuery = { [key: string]: string };

type Props = CorrectProps | ErrorProps;

interface ErrorProps {
  code: number;
  message: string;
}

interface CorrectProps {
  initialApolloState?: NormalizedCacheObject;
  layer: LayerInfo;
  query: UrlQuery;
  layerData: GetInfoByArea;
  nearbyAreas: GetNearbyAreasQuery;
  nearbyDistricts: GetNearbyAreasQuery | undefined;
  trends: GetTrendsQuery | undefined;
  urlQuery?: string;
  widgetXId?: string;
  gtmId?: string;
  gaMeasurementId?: string;
  market?: LocaleValue;
  faqData?: FAQSectionData;
  canonicalUrl?: string;
  headerSettings: THeaderSettings;
}

type State = {
  widgetXId?: string;
  headerSettings?: THeaderSettings;
};

const IndexPage = observer(
  class IndexPage extends Component<Props, State> {
    state: State = {};

    componentDidMount(): void {
      this.setState({
        ...this.state,
        widgetXId: (this.props as CorrectProps).widgetXId,
        headerSettings: (this.props as CorrectProps).headerSettings,
      });
      UIStore.registerFilters(FilterStore, { debounce: 500 });
      sendImpressionEvent();
    }

    constructor(props: Props) {
      super(props);

      if ('code' in props) {
        return;
      }

      LayerStore.setData(props.layerData);
      if (props.nearbyDistricts) {
        LayerStore.setNearbyData(props.nearbyDistricts, props.layer.layerType);
        LayerStore.setNearbyData(props.nearbyAreas, Layer.City);
      } else {
        LayerStore.setNearbyData(props.nearbyAreas, props.layer.layerType);
      }
      if (props.trends) {
        LayerStore.setTrends(props.trends);
      }

      // Initialize filters after full page reload
      initFilters(props.layerData, props.layer, props.query);
    }

    static async getInitialProps({
      asPath = '/',
      query,
      res,
      req,
    }: NextPageContext): Promise<Partial<Props>> {
      const slugs = Array.isArray(query.slugs) ? '/' + query.slugs.join('/') : '';
      const canonicalUrl = config().publicRuntimeConfig.host + slugs;

      const apolloClient = createApolloClient();
      const mapPlace = await MapPlace.fromUrl(asPath, apolloClient);
      const [urlPath, urlQuery] = asPath.split('?');

      if (!mapPlace) {
        // eslint-disable-next-line no-console
        console.log(`McCompass: Unable to fetch data from ${urlPath}. Fullpath: ${asPath}`);
        return handleError(res);
      }

      if (mapPlace.url !== urlPath) {
        let seoUrl = mapPlace.url;
        if (urlQuery !== undefined) {
          seoUrl += `?${urlQuery}`;
        }

        // eslint-disable-next-line no-console
        console.log(
          `Actual (${urlPath}) and SEO (${mapPlace.url}) paths are different. Redirecting to: ${seoUrl}`,
        );

        if (res) {
          res.writeHead(302, {
            Location: seoUrl,
          });
          res.end();
        } else {
          Router.replace('/', seoUrl, {
            shallow: true,
          });
        }
      }

      const parentSearch: ParentSearch = getParentSearch(mapPlace);
      const queryVariables: GetInfoByAreaQueryVariables = {
        layer: mapPlace.layerType,
        name: mapPlace.layerName,
        parentSearch,
      };

      const nearbyAreaQueryVariables = getVariablesOfNearbyAreas(mapPlace);
      const layerData = await LayerStore.getLayer(apolloClient, queryVariables);
      if (!layerData.infoByArea) {
        return handleError(res);
      }

      const nearbyAreas = await LayerStore.getNearbyAreas(apolloClient, nearbyAreaQueryVariables);
      let nearbyDistricts: GetNearbyAreasQuery | undefined;
      let trends: GetTrendsQuery | undefined;
      if (mapPlace.layerType === Layer.District || mapPlace.layerType === Layer.DistrictSmall) {
        nearbyDistricts = await LayerStore.getNearbyAreas(apolloClient, queryVariables);
      }
      if (mapPlace.layerType !== Layer.Country) {
        trends = await LayerStore.getTrends(apolloClient, queryVariables);
      }

      // This is used to initialize filters after explicit push route is done, eg: search or link click
      if (process.browser) {
        document.dispatchEvent(new Event('mcm:pushRouteComplete'));
        initFilters(layerData, mapPlace, query as UrlQuery);
        moveMap(apolloClient);
      }

      let widgetXId, gtmId, gaMeasurementId, market, headerSettings;
      // Avoiding making request this in the browser
      if (typeof window === 'undefined' && req) {
        market = getMarket(req.headers.host);
        const webSettings = await getWebSettings(market);
        ({ widgetXId, gtmId, gaMeasurementId, headerSettings } = webSettings);
      }

      const faqData = await getFAQData();

      return {
        initialApolloState: apolloClient.cache.extract(),
        layer: mapPlace.toJson(),
        query: query as UrlQuery,
        layerData: layerData as GetInfoByArea,
        widgetXId,
        gtmId,
        gaMeasurementId,
        market,
        nearbyAreas,
        nearbyDistricts,
        trends,
        faqData,
        urlQuery,
        canonicalUrl,
        headerSettings,
      };
    }

    render(): JSX.Element {
      if ('code' in this.props) {
        return <NextError title={this.props.message} statusCode={this.props.code} />;
      }
      const { layer, layerData, gtmId, gaMeasurementId, market, canonicalUrl } = this.props;
      const widgetXId = this.props.widgetXId || this.state.widgetXId;
      const coordinates = (layerData?.infoByArea?.center as { coordinates: LatLngCoordinates })
        .coordinates;
      const center = filterViewModel.center || coordinates;

      const sharingImage = `${MAPBOX_IMAGE_BASE_URL}${center.join(',') || ''},${
        ZOOM_LEVELS[layer.layerType]
      },0/600x600?access_token=${MAPBOX_ACCESS_TOKEN}`;

      const metaContent = getMetaContent(layer);

      return (
        <>
          <Layout
            url={layer.url}
            title={metaContent.title}
            metaDescription={metaContent.description}
            sharingImage={sharingImage}
            gtmId={gtmId}
            gaMeasurementId={gaMeasurementId}
            layer={layer}
            canonicalUrl={canonicalUrl}
          >
            <Header headerSettings={this.state.headerSettings || this.props.headerSettings} />
            <Wrapper theme={defaultTheme}>
              <Map layerType={layer.layerType} coordinates={center} />
              <GradientScale className="show-on-desktop" />
              <GetInTouchBanner
                buttonText={'Kundenberatung anfordern'}
                className={`${LayerStore.positionSelected ? 'point-selected' : ''}`}
              />
              <ReviewBar />
            </Wrapper>
            <ContentSection layer={layer} layerData={layerData} faqData={this.props.faqData} />
            <OverlayCmp>
              <GetInTouchButton buttonText={'Immobilienbewertung starten'} />
            </OverlayCmp>
            <Footer className="show-on-desktop" />
          </Layout>
          {LayerStore.showWidgetX && (
            <Modal className="modal">
              <WidgetX
                apiToken={config().publicRuntimeConfig.storyblokWidgetKey}
                market={getLocaleForWidget(market)}
                id={widgetXId || 'default'}
                widgetXBaseApi={'/'}
                onClose={() => LayerStore.setVisibilityWidgetX(false)}
                showCloseIcon
              />
            </Modal>
          )}
        </>
      );
    }
  },
);

function getParentSearch(layer: LayerInfo): ParentSearch {
  const { layerType } = layer;

  if (layerType === Layer.DistrictSmall) {
    return {
      state: layer.state,
      city: layer.city,
      district: layer.district,
    };
  }

  if (layerType === Layer.District) {
    return {
      state: layer.state,
      city: layer.city,
    };
  }

  if (layerType === Layer.City) {
    return {
      state: layer.state,
    };
  }

  return {};
}

function initFilters(layerData: GetInfoByAreaQuery, layer: LayerInfo, query: UrlQuery): void {
  // Set default filters for first render or explicitly push route
  UIStore.unregisterFilters();
  const queryCenter = sanitizeQueryParam(query.center);
  const queryMarker = sanitizeQueryParam(query.marker);

  filterViewModel.center =
    queryCenter?.split(',')?.length === 2
      ? (queryCenter.split(',').map((v) => Number(v)) as LatLngCoordinates)
      : (layerData?.infoByArea?.center as { coordinates: LatLngCoordinates }).coordinates;
  filterViewModel.zoom = (query.zoom && parseInt(query.zoom, 10)) || ZOOM_LEVELS[layer.layerType];
  filterViewModel.marker =
    queryMarker?.split(',')?.length === 2
      ? (query.marker.split(',').map((v) => Number(v)) as LatLngCoordinates)
      : null;

  try {
    filterViewModel.submit();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(
      e instanceof Error ? e.message : '',
      JSON.stringify({
        query,
        current: {
          center: filterViewModel.center,
          marker: filterViewModel.marker,
        },
        model: filterViewModel.model,
      }),
    );
  }
}

function getVariablesOfNearbyAreas(layer: LayerInfo): GetNearbyAreasQueryVariables {
  if (layer.layerType === Layer.State) {
    return {
      layer: Layer.State,
      name: layer.layerName,
      parentSearch: {},
    };
  }

  return {
    layer: Layer.City,
    name: layer.city || layer.layerName,
    parentSearch: {
      state: layer.state,
    },
  };
}

function handleError(res?: NextPageContext['res']): ErrorProps {
  if (res) {
    res.statusCode = 404;
  }

  return {
    code: 404,
    message: 'Not found',
  };
}

export default IndexPage;
